import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatPrice } from "@/lib/api";
import { SignaturePadField } from "@/components/SignaturePadField";
import { PromoField } from "@/components/PromoField";
import { toast } from "sonner";

const AGREEMENT_EN = `This Short-Term Rental Agreement is entered into between StayTLV (Host) and the Guest named below, for the dates and property listed.

1. Use of Property. The property shall be used solely as a private vacation residence for the named Guest. Parties, events, and commercial activity are not permitted.

2. Payment & Deposit. The Guest agrees to pay the total due upon booking. A refundable security deposit may be held or pre-authorized separately and returned within 14 days of check-out, less any deductions for damages.

3. Cancellation. Cancellations more than 30 days before check-in are refunded minus a 5% processing fee. Cancellations within 30 days of arrival are non-refundable.

4. Check-in / Check-out. Check-in: 15:00. Check-out: 11:00.

5. House Rules. Guest agrees to abide by the house rules provided, including respect for neighbours and quiet hours.

6. Liability. Guest is liable for damages caused beyond normal wear and tear.`;

const AGREEMENT_HE = `הסכם שכירות קצרת מועד זה נחתם בין StayTLV (המארח) לבין האורח הנקוב להלן, עבור הדירה והתאריכים המפורטים.

1. שימוש בדירה. הדירה תשמש למגורי נופש פרטיים בלבד עבור האורח. אין לקיים מסיבות, אירועים או פעילות מסחרית.

2. תשלום ופיקדון. האורח מסכים לשלם את הסכום המלא בעת ההזמנה. פיקדון ביטחון מוחזר יוחזק בנפרד ויוחזר תוך 14 יום מהיציאה, בניכוי נזקים אם יהיו.

3. ביטולים. ביטולים יותר מ-30 יום לפני הכניסה יוחזרו בניכוי 5% עמלת עיבוד. ביטולים תוך 30 יום אינם בני החזרה.

4. כניסה/יציאה. כניסה: 15:00. יציאה: 11:00.

5. כללי הבית. האורח מסכים לכבד את כללי הבית ושעות השקט.

6. אחריות. האורח אחראי על נזקים מעבר לבלאי טבעי.`;

export default function Checkout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [guest, setGuest] = useState({ first_name: "", last_name: "", email: "", phone: "", country: "", notes: "" });
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const agreement = i18n.language === "he" ? AGREEMENT_HE : AGREEMENT_EN;

  useEffect(() => {
    api.get(`/bookings/${id}`).then(r => setData(r.data)).catch(() => navigate("/"));
  }, [id, navigate]);

  if (!data) return <div className="py-32 text-center text-[#8C7A6B]">Loading…</div>;
  const { booking, listing } = data;
  const lang = i18n.language;
  const listingName = lang === "he" ? listing.name_he : listing.name_en;

  const applyPromo = async (code) => {
    const r = await api.post(`/bookings/${booking.booking_id}/apply-promo`, { code });
    setData({ ...data, booking: r.data });
    toast.success(t("detail.promo_applied"));
  };
  const removePromo = async () => {
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/apply-promo`, { code: null });
      setData({ ...data, booking: r.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove code");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!signature) { toast.error(t("checkout.must_sign")); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/sign-and-checkout`, {
        booking_id: booking.booking_id,
        guest,
        signature_data_url: signature,
        agreement_text: agreement,
        origin_url: window.location.origin,
      });
      // r.data.step is "deposit" (caution auth first) or "payment" (no deposit, direct pay)
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <p className="overline mb-3">{t("checkout.title")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2C2A29]">{t("checkout.title")}</h1>
      <p className="text-[#595450] mt-2">{t("checkout.sub")}</p>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-12">
        <div className="lg:col-span-2 space-y-10">
          {/* Guest details */}
          <section className="bg-white rounded-2xl p-8 border border-[#EAE3D9]">
            <h2 className="font-serif text-2xl text-[#2C2A29] mb-6">{t("admin.guest")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={t("checkout.first_name")} value={guest.first_name} onChange={(v) => setGuest({ ...guest, first_name: v })} testid="first-name" required />
              <Field label={t("checkout.last_name")} value={guest.last_name} onChange={(v) => setGuest({ ...guest, last_name: v })} testid="last-name" required />
              <Field label={t("checkout.email")} value={guest.email} onChange={(v) => setGuest({ ...guest, email: v })} testid="email" type="email" required />
              <Field label={t("checkout.phone")} value={guest.phone} onChange={(v) => setGuest({ ...guest, phone: v })} testid="phone" required />
              <Field label={t("checkout.country")} value={guest.country} onChange={(v) => setGuest({ ...guest, country: v })} testid="country" />
              <Field label={t("checkout.notes")} value={guest.notes} onChange={(v) => setGuest({ ...guest, notes: v })} testid="notes" />
            </div>
          </section>

          {/* Agreement + signature */}
          <section className="bg-white rounded-2xl p-8 border border-[#EAE3D9]">
            <h2 className="font-serif text-2xl text-[#2C2A29] mb-2">{t("checkout.agreement_title")}</h2>
            <p className="text-sm text-[#8C7A6B] mb-4">{t("checkout.agreement_intro")}</p>
            <div className="max-h-64 overflow-y-auto bg-[#FDFBF7] border border-[#EAE3D9] rounded-xl p-5 text-sm text-[#595450] leading-relaxed whitespace-pre-wrap mb-6" data-testid="agreement-text">
              {agreement}
            </div>
            <SignaturePadField onChange={setSignature} />
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 bg-white rounded-2xl p-7 border border-[#EAE3D9]" data-testid="checkout-summary">
            <img src={listing.cover_image} alt={listingName} className="w-full h-40 object-cover rounded-xl mb-5" />
            <h3 className="font-serif text-xl text-[#2C2A29]">{listingName}</h3>
            <p className="text-sm text-[#8C7A6B]">{lang === "he" ? listing.neighborhood_he : listing.neighborhood_en}</p>

            <div className="mt-5 pt-5 border-t border-[#EAE3D9] space-y-2 text-sm text-[#595450]">
              <Row label={t("detail.check_in")} value={booking.check_in} />
              <Row label={t("detail.check_out")} value={booking.check_out} />
              <Row label={t("detail.guests")} value={booking.guests} />
              <Row label={`${formatPrice(booking.nightly_rate, booking.currency)} × ${booking.nights}`} value={formatPrice(booking.subtotal, booking.currency)} />
              <Row label={t("detail.cleaning")} value={formatPrice(booking.cleaning_fee, booking.currency)} />
              {booking.discount_amount > 0 && (
                <div className="flex justify-between text-[#3F5036]" data-testid="checkout-discount-row">
                  <span>{t("detail.promo_discount", { code: booking.promo_code })}</span>
                  <span>-{formatPrice(booking.discount_amount, booking.currency)}</span>
                </div>
              )}
            </div>
            <div className="mt-3">
              <PromoField applied={booking.promo_code} onApply={applyPromo} onRemove={removePromo} disabled={submitting} />
            </div>
            <div className="mt-3 pt-3 border-t border-[#EAE3D9] flex justify-between text-sm text-[#595450]">
              <span>{t("detail.total")}</span>
              <span>{formatPrice(booking.total_due_now, booking.currency)}</span>
            </div>
            {booking.security_deposit > 0 && (
              <div className="flex justify-between text-xs text-[#8C7A6B] mt-1">
                <span>+ {t("detail.deposit")}</span>
                <span>{formatPrice(booking.security_deposit, booking.currency)}</span>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-[#EAE3D9] flex justify-between font-medium text-[#2C2A29]">
              <span>{t("detail.total_to_authorize")}</span>
              <span className="font-serif text-2xl">{formatPrice((booking.total_due_now + (booking.security_deposit || 0)), booking.currency)}</span>
            </div>
            {booking.security_deposit > 0 && (
              <p className="text-[10px] text-[#8C7A6B] leading-relaxed mt-2">{t("detail.deposit_note")}</p>
            )}

            <button type="submit" data-testid="pay-btn" disabled={submitting}
                    className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "..." : t("cta.pay")}
            </button>
            <p className="text-xs text-[#8C7A6B] text-center mt-3">Secured by Stripe · SSL encrypted</p>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, type = "text", required }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{label}</span>
    <input
      data-testid={`guest-${testid}-input`}
      type={type}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      className="input-line"
    />
  </label>
);

const Row = ({ label, value, muted }) => (
  <div className={`flex justify-between ${muted ? "text-[#8C7A6B]" : ""}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);
